import React, { useState, useEffect } from 'react';

// Import ProductCard
import ProductCard from './products/ProductCard';
import CategoryCarousel from './products/CategoryCarousel';
import LocationSelector from './common/LocationSelector';
import { callApi } from './api/api';

const LandingPageView = ({ showPage, products, addToCart, searchTerm, setSearchTerm, setSelectedTherapyAreaFilter, userLocation, setUserLocation, userCoordinates, setUserCoordinates }) => {
    const [suggestions, setSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);

    useEffect(() => {
        const fetchSuggestions = async () => {
            if (searchTerm.length < 2) {
                setSuggestions([]);
                setShowSuggestions(false);
                return;
            }

            try {
                const data = await callApi(`/v2/products/suggestions?search=${searchTerm}`, 'GET');
                setSuggestions(data || []);
                setShowSuggestions(data && data.length > 0);
            } catch (error) {
                console.error("Failed to fetch suggestions", error);
                setSuggestions([]);
            }
        };

        const timeoutId = setTimeout(fetchSuggestions, 300); // Debounce
        return () => clearTimeout(timeoutId);
    }, [searchTerm]);

    const handleSearchSubmit = (e) => {
        e.preventDefault();
        setShowSuggestions(false);
        showPage('products', { keepFilters: true });
    };

    const handleSuggestionClick = (name) => {
        setSearchTerm(name);
        setShowSuggestions(false);
        // Small delay to allow state update before navigation if needed, 
        // but here we just navigate to products page which will trigger fetch with new searchTerm
        showPage('products', { keepFilters: true });
    };

    const handleCategoryClick = (categoryName) => {
        setSelectedTherapyAreaFilter(categoryName);
        showPage('products', { keepFilters: true });
    };

    return (
        <section id="landing" className="animate-fade-in-up">
            {/* Hero Section */}
            <div className="relative bg-gradient-to-r from-sky-600 to-sky-800 rounded-3xl mb-12 shadow-2xl">
                <div className="absolute inset-0 bg-black opacity-10 rounded-3xl"></div>
                <div className="relative p-6 md:p-10 lg:px-12 lg:py-12 text-white text-center md:text-left flex flex-col md:flex-row items-center justify-between">
                    <div className="md:w-3/5 z-20">
                        <h1 className="text-3xl md:text-4xl lg:text-5xl font-extrabold mb-3 tracking-tight leading-tight">
                            Your Local Pharmacy,<br/>
                            <span className="text-sky-200">Delivered Fast.</span>
                        </h1>
                        <p className="text-base md:text-lg text-sky-100 mb-6 max-w-lg mx-auto md:mx-0 font-medium">
                            Genuine medicines, exact local prices, and fastest delivery in the neighborhood.
                        </p>
                        
                        {/* Search Box in Hero */}
                        <div className="relative w-full max-w-3xl mx-auto md:mx-0 shadow-lg rounded-full bg-white flex items-center h-14">
                            <form onSubmit={handleSearchSubmit} className="relative z-20 flex-1 flex items-center h-full w-full">
                                <LocationSelector userLocation={userLocation} setUserLocation={setUserLocation} userCoordinates={userCoordinates} setUserCoordinates={setUserCoordinates} className="rounded-l-full" />
                                <div className="flex-1 relative h-full">
                                    <input
                                        type="text"
                                        placeholder="Search medicines or health products..."
                                        className="w-full h-full px-5 py-3 pl-10 rounded-r-full text-gray-800 focus:outline-none focus:bg-sky-50/30 text-sm bg-transparent"
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
                                        onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                                    />
                                    <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                                        <svg className="h-5 w-5 text-sky-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                        </svg>
                                    </div>
                                    <button
                                        type="submit"
                                        className="absolute right-2 top-2 bottom-2 bg-sky-600 hover:bg-sky-700 text-white flex items-center justify-center w-10 rounded-full transition-all duration-300"
                                    >
                                        <i className="fas fa-search text-base"></i>
                                    </button>
                                </div>
                            </form>

                            {/* Autocomplete Suggestions */}
                            {showSuggestions && (
                                <div className="absolute top-full left-0 right-0 mt-3 bg-white rounded-2xl shadow-2xl border border-gray-100 z-50 overflow-hidden text-left">
                                    {suggestions.map((name, index) => (
                                        <button
                                            key={index}
                                            onClick={() => handleSuggestionClick(name)}
                                            className="w-full text-left px-6 py-3 hover:bg-sky-50 transition-colors flex items-center gap-3 border-b last:border-0 border-gray-50"
                                        >
                                            <i className="fas fa-search text-gray-300 text-xs"></i>
                                            <span className="text-gray-700 font-medium text-sm">{name}</span>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                    
                    {/* Hero Illustration (Placeholder/Abstract) */}
                    <div className="hidden md:flex w-2/5 justify-end z-10 relative">
                        <div className="w-64 h-64 bg-white/10 rounded-full blur-3xl absolute -right-10 -top-10"></div>
                        <i className="fas fa-heartbeat text-[12rem] text-white/20 transform rotate-12 drop-shadow-2xl"></i>
                    </div>
                </div>
            </div>

            {/* Quick Actions (Modern Tiles) */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-16 px-2">
                <button
                    onClick={() => showPage('upload-prescription')}
                    className="group bg-white hover:bg-emerald-50 border border-slate-100 p-6 rounded-3xl transition-all duration-300 shadow-sm hover:shadow-lg flex flex-col items-center justify-center relative overflow-hidden"
                >
                    <div className="absolute inset-0 bg-gradient-to-br from-emerald-100/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                    <div className="bg-emerald-100 text-emerald-600 p-4 rounded-2xl mb-4 group-hover:scale-110 transition-transform">
                        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                    </div>
                    <span className="text-lg font-bold text-slate-800">Order via Prescription</span>
                    <span className="text-sm text-slate-500 mt-1">Upload image to order</span>
                </button>
                
                <button
                    onClick={() => showPage('products')}
                    className="group bg-white hover:bg-purple-50 border border-slate-100 p-6 rounded-3xl transition-all duration-300 shadow-sm hover:shadow-lg flex flex-col items-center justify-center relative overflow-hidden"
                >
                    <div className="absolute inset-0 bg-gradient-to-br from-purple-100/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                    <div className="bg-purple-100 text-purple-600 p-4 rounded-2xl mb-4 group-hover:scale-110 transition-transform">
                        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                        </svg>
                    </div>
                    <span className="text-lg font-bold text-slate-800">Find Substitutes</span>
                    <span className="text-sm text-slate-500 mt-1">Save on generic medicines</span>
                </button>
                
                <button
                    onClick={() => showPage('orders')}
                    className="group bg-white hover:bg-orange-50 border border-slate-100 p-6 rounded-3xl transition-all duration-300 shadow-sm hover:shadow-lg flex flex-col items-center justify-center relative overflow-hidden"
                >
                    <div className="absolute inset-0 bg-gradient-to-br from-orange-100/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity"></div>
                    <div className="bg-orange-100 text-orange-600 p-4 rounded-2xl mb-4 group-hover:scale-110 transition-transform">
                        <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                    </div>
                    <span className="text-lg font-bold text-slate-800">Refill Medicines</span>
                    <span className="text-sm text-slate-500 mt-1">Reorder from past purchases</span>
                </button>
            </div>

            {/* Categories Section */}
            <CategoryCarousel onCategoryClick={handleCategoryClick} />

        </section>
    );
};

export default LandingPageView;